import React, { useEffect, useMemo, useRef, useState } from "react";

// AG Grid v34
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-quartz.css";

import {
  Box,
  Heading,
  Flex,
  Image,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Button,
  Spacer,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  VStack,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import {
  HiOutlinePlus,
  HiOutlineSave,
  HiOutlineSearch,
} from "react-icons/hi";

import Navbar from "./Navbar";
import HeroImage from "./images/stamps.png";
import MiniRichTextEditor from "./components/MiniRichTextEditor";
import { getSources, createSource, updateSource } from "./services/SourcesAPI";

import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
ModuleRegistry.registerModules([AllCommunityModule]);

/* ---------------------------------------
   Helpers
----------------------------------------*/
const uuid = () =>
  (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

const newSourceTemplate = () => ({
  _id: undefined,
  type: "",
  heading: "",
  value: "",
  _isNew: true,
});

/* ---------------------------------------
   Component
----------------------------------------*/
const Sources = () => {
  const toast = useToast();
  const gridRef = useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [sources, setSources] = useState([]);
  const [editedSource, setEditedSource] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  /* ---------------------------------------
     Load all sources
  ----------------------------------------*/
  const refreshSources = async () => {
    try {
      const data = await getSources();
      setSources(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error refreshing sources", err);
      toast({
        title: "Error loading sources",
        status: "error",
        duration: 3000,
      });
    }
  };

  useEffect(() => {
    refreshSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------------------------------
     Handle row click to edit
  ----------------------------------------*/
  const handleRowClick = (params) => {
    const source = params.data;
    setEditedSource({ ...source });
    setIsDirty(false);
    onOpen();
  };

  /* ---------------------------------------
     Create new source
  ----------------------------------------*/
  const handleAddSource = () => {
    const newSource = newSourceTemplate();
    setEditedSource(newSource);
    setIsDirty(true);
    onOpen();
  };

  /* ---------------------------------------
     Save source (POST/PUT)
  ----------------------------------------*/
  const handleSave = async () => {
    if (!editedSource) return;

    // Validation
    if (!editedSource.type?.trim()) {
      toast({
        title: "Type is required",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    if (!editedSource.heading?.trim()) {
      toast({
        title: "Heading is required",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    const isNew = editedSource._isNew === true;
    setIsSaving(true);

    try {
      const payload = {
        type: editedSource.type,
        heading: editedSource.heading,
        value: editedSource.value || "",
      };

      // For update, include _id
      if (!isNew && editedSource._id) {
        payload._id = editedSource._id;
      }

      if (isNew) {
        await createSource(payload);
      } else {
        await updateSource(editedSource._id, payload);
      }

      toast({
        title: isNew ? "Source created" : "Source updated",
        description: `Successfully saved "${editedSource.heading}"`,
        status: "success",
        duration: 3000,
      });

      // Refresh the grid
      await refreshSources();

      // Close the modal
      onClose();
      setEditedSource(null);
      setIsDirty(false);
    } catch (err) {
      console.error("Save error:", err);
      toast({
        title: "Error saving source",
        status: "error",
        description: err?.message || "Unknown error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  /* ---------------------------------------
     Handle modal close
  ----------------------------------------*/
  const handleModalClose = () => {
    if (isDirty) {
      if (!window.confirm("You have unsaved changes. Are you sure you want to close?")) {
        return;
      }
    }
    onClose();
    setEditedSource(null);
    setIsDirty(false);
  };

  /* ---------------------------------------
     AG Grid config
  ----------------------------------------*/
  const columnDefs = useMemo(
    () => [
      {
        headerName: "ID",
        field: "_id",
        width: 280,
        resizable: true,
      },
      {
        headerName: "Type",
        field: "type",
        width: 180,
        resizable: true,
      },
      {
        headerName: "Heading",
        field: "heading",
        flex: 1,
        minWidth: 200,
      },
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      floatingFilter: true,
      resizable: true,
      suppressHeaderMenuButton: false,
    }),
    []
  );

  const autoSizeStrategy = useMemo(
    () => ({ type: "fitGridWidth", defaultMinWidth: 100 }),
    []
  );

  const rowData = useMemo(() => sources, [sources]);

  const onGridReady = () => {
    // Grid is ready
  };

  /* ---------------------------------------
     Render
  ----------------------------------------*/
  return (
    <Box height="100vh" display="flex" flexDirection="column" overflow="hidden">
      <Navbar />

      <Box position="relative" width="100%" flex="1" overflow="hidden">
        <Image
          src={HeroImage}
          alt="Hero Watermark"
          width="100%"
          height="100%"
          objectFit="cover"
          opacity={0.35}
          position="absolute"
          top={0}
          left={0}
          zIndex={-1}
          filter="blur(1px)"
        />

        <Box position="relative" zIndex={1} p={10} height="100%" display="flex" flexDirection="column" overflow="hidden">
          {/* Toolbar */}
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={3}
            mb={3}
            borderColor="gray.500"
            bg="whiteAlpha.800"
            flexShrink={0}
          >
            <Flex align="center" mb={3}>
              <Heading size="lg">Sources</Heading>
              <Spacer />
              <Button
                leftIcon={<HiOutlinePlus />}
                colorScheme="blue"
                onClick={handleAddSource}
              >
                Create
              </Button>
            </Flex>

            <InputGroup bg="white">
              <InputLeftElement pointerEvents="none">
                <HiOutlineSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search sources..."
                value={searchTerm}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchTerm(val);
                  gridRef.current?.api?.setGridOption("quickFilterText", val);
                }}
              />
            </InputGroup>
          </Box>

          {/* Grid */}
          <Box
            borderWidth="1px"
            borderRadius="md"
            p={1}
            borderColor="gray.500"
            bg="whiteAlpha.800"
            flex="1"
            overflow="hidden"
          >
            <div
              className="ag-theme-quartz"
              style={{ width: "100%", height: "100%" }}
            >
              <AgGridReact
                ref={gridRef}
                theme="quartz"
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowData={rowData}
                autoSizeStrategy={autoSizeStrategy}
                rowSelection={{ mode: "singleRow", checkboxes: false }}
                suppressRowClickSelection={false}
                getRowId={(p) => p.data?._id || uuid()}
                pagination
                paginationPageSize={50}
                animateRows
                onGridReady={onGridReady}
                onRowClicked={handleRowClick}
              />
            </div>
          </Box>
        </Box>
      </Box>

      {/* Edit Modal */}
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        size="full"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent m={8} maxW="calc(100% - 4rem)">
          <ModalHeader>
            {editedSource?._isNew ? "Create New Source" : "Edit Source"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {editedSource && (
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Type</FormLabel>
                  <Input
                    value={editedSource.type || ""}
                    onChange={(e) => {
                      setEditedSource({ ...editedSource, type: e.target.value });
                      setIsDirty(true);
                    }}
                    placeholder="type of contract heading e.g premium, situation, interest, etc."
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Heading</FormLabel>
                  <Input
                    value={editedSource.heading || ""}
                    onChange={(e) => {
                      setEditedSource({ ...editedSource, heading: e.target.value });
                      setIsDirty(true);
                    }}
                    placeholder="The HEADING text for the source type..."
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Value (Rich Text)</FormLabel>
                  <MiniRichTextEditor
                    value={editedSource.value || ""}
                    onChange={(newValue) => {
                      setEditedSource({ ...editedSource, value: newValue });
                      setIsDirty(true);
                    }}
                    placeholder="Enter source value with formatting..."
                    minHeight="250px"
                  />
                </FormControl>

                <Flex justify="flex-end" gap={3} pt={4}>
                  <Button variant="ghost" onClick={handleModalClose}>
                    Cancel
                  </Button>
                  <Button
                    leftIcon={<HiOutlineSave />}
                    onClick={handleSave}
                    isLoading={isSaving}
                    colorScheme={isDirty ? "blue" : undefined}
                    variant={isDirty ? "solid" : "outline"}
                  >
                    Save
                  </Button>
                </Flex>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Sources;
